/**
 * "Run it live": from a track to a running room in one act.
 *
 * Takes what the track already holds (its people, its pool, its locked
 * criteria), checks the same readiness the run RPC will check when the room
 * closes (so the host hears "not yet" now, with the fix named, instead of
 * "Prerequisites not met" after the room has voted), creates the session
 * through the one governed create path, seats the participants, and hands back
 * the room's address. The live board does the rest when it opens with
 * `?startRanking=1`: it creates the tool session, takes the session live,
 * tells the participants, and opens the gate.
 */
import { createSessionWithFeedback, describeCreateSessionFailure, type ShowToastFn } from '@/lib/sessions/createSessionFlow';
import { getCurrentWorkspaceId } from '@/lib/supabaseSync';
import { evaluateTrackReadiness, type TrackReadinessStatus } from '@/lib/trackReadinessEvaluator';
import { useSessionParticipantsStore } from '@/stores/prioritization/sessionParticipantsStore';

export interface RunBlocker {
  /** One plain line: what has to be true first. */
  line: string;
  /** The readiness step that is not ready, as the evaluator names it. */
  step: 's1_participation' | 's2_pool' | 's3_criteria' | 's4_scales' | 's5_values' | 's6_tool';
  /** The track tab where that is fixed, or null when no tab fixes it. */
  tab: 'participation' | 'pool' | 'criteria' | 'value-ranges' | 'values' | null;
}

const STEP_TABS: Record<RunBlocker['step'], RunBlocker['tab']> = {
  s1_participation: 'participation',
  s2_pool: 'pool',
  s3_criteria: 'criteria',
  s4_scales: 'value-ranges',
  s5_values: 'values',
  s6_tool: null,
};

const STEP_FALLBACK_LINES: Record<RunBlocker['step'], string> = {
  s1_participation: 'Add participants to this track first.',
  s2_pool: "Put items in the track's pool and lock it first.",
  s3_criteria: "Lock the track's criteria first.",
  s4_scales: "Accept the track's value ranges first.",
  s5_values: 'Enter the values the ranking compares on first.',
  s6_tool: 'Choose a method first.',
};

/** The first readiness condition that is not met, in plain words, or null when the track is ready. */
export function describeRunBlocker(status: TrackReadinessStatus | null | undefined): RunBlocker | null {
  if (!status || status.allPrerequisitesMet) return null;
  const steps = Object.keys(STEP_TABS) as RunBlocker['step'][];
  for (const step of steps) {
    const condition = status[step];
    if (!condition || condition.ready) continue;
    const line = condition.actions[0] ?? condition.blockers[0] ?? STEP_FALLBACK_LINES[step];
    return { line, step, tab: STEP_TABS[step] };
  }
  return { line: 'This track is not ready to run yet.', step: 's6_tool', tab: null };
}

/** The session's name: the track, the word for what it is, and the day. */
export function runSessionName(trackName: string, when: Date = new Date()): string {
  const day = when.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return `${trackName}: live ranking, ${day}`;
}

/** The room's address: the live board, told to open the tool session itself. */
export function roomPathFor(sessionId: string): string {
  return `/prioritization/sessions/${sessionId}/live?startRanking=1`;
}

/** Where a participant joins from a phone; the Board prints this beside its QR code. */
export function joinPathFor(sessionId: string): string {
  return `/prioritization/sessions/${sessionId}/participate`;
}

export type RunItLiveResult =
  | { ok: true; sessionId: string; roomPath: string }
  | { ok: false; blocker: RunBlocker }
  | { ok: false; error: string };

export async function startRunItLive(input: {
  track: { id: string; name: string; cycleId: string | null };
  /** The person starting the room; they host it. */
  userId: string;
  /** The track's effective participants. The host is seated whether or not they are among them. */
  participantIds: string[];
  /** The track pool's items. */
  itemIds: string[];
  /** The track's accepted criteria, by attribute key. */
  criteriaIds: string[];
  /** The track's weights for those criteria (migration 1047), when it sets them. */
  criteriaWeights?: Record<string, number>;
  /** Registry method id, e.g. `dot_voting`. */
  toolId: string;
  toast?: ShowToastFn;
  now?: Date;
}): Promise<RunItLiveResult> {
  const { track, userId } = input;

  // The same check the run RPC makes at close, asked first.
  const readiness = await evaluateTrackReadiness(track.id);
  const blocker = describeRunBlocker(readiness);
  if (blocker) return { ok: false, blocker };

  const created = await createSessionWithFeedback(
    {
      name: runSessionName(track.name, input.now),
      facilitatorId: userId,
      createdBy: userId,
      cycleId: track.cycleId ?? undefined,
      trackId: track.id,
      sessionContextMode: track.cycleId ? 'cycle_linked' : 'app_linked',
      intent: 'rank_priorities',
      initialCriteriaIds: input.criteriaIds,
      initialCriteriaWeights: input.criteriaWeights,
      initialItemIds: input.itemIds,
      selectedToolId: input.toolId,
      modeConfig: { timingMode: 'live', entryMode: 'facilitator' },
      workspaceId: getCurrentWorkspaceId() ?? undefined,
    },
    { toast: input.toast },
  );
  if (!created.ok || !created.sessionId) {
    return { ok: false, error: describeCreateSessionFailure(created.error, created.blockedBy) };
  }
  const sessionId = created.sessionId;

  // Seat the track's people. session_participants is the roster of record; the
  // live board reads it to create the tool session and to tell people.
  const seats = useSessionParticipantsStore.getState();
  try {
    await seats.fetchParticipantsForSession(sessionId);
  } catch {
    // Offline or refused: the adds below still try.
  }
  const seated = new Set(seats.getActiveParticipantIdsForSession(sessionId));
  for (const participantId of new Set(input.participantIds)) {
    if (!participantId || participantId === userId || seated.has(participantId)) continue;
    try {
      await seats.addParticipant({ sessionId, userId: participantId, role: 'contributor', addedBy: userId });
    } catch (err) {
      console.warn('[runItLive] could not seat participant', participantId, err);
    }
  }

  return { ok: true, sessionId, roomPath: roomPathFor(sessionId) };
}
